import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Chip,
  Divider,
  FormGroup,
  FormControlLabel,
  Switch,
  Card,
  CardContent
} from '@mui/material';
import { 
  Face as FaceIcon, 
  Search as SearchIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import faceRecognitionService from '../services/faceRecognitionService';
import AuthenticatedImage from './AuthenticatedImage';

/**
 * Dialog to display face recognition matches for reference persons in a specific image
 */
const SingleImageFaceRecognitionDialog = ({ open, onClose, sessionId, image, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [expandedPersons, setExpandedPersons] = useState({});
  const [hasExistingMatches, setHasExistingMatches] = useState(false);
  const [showExistingResults, setShowExistingResults] = useState(true);
  const [performedFreshRecognition, setPerformedFreshRecognition] = useState(false); // Track if new recognition ran
  
  // Face recognition search mode options
  const [searchMainFace, setSearchMainFace] = useState(true);
  const [searchAllFaces, setSearchAllFaces] = useState(true);
  const [showSearchOptions, setShowSearchOptions] = useState(false);

  // Maximum number of matches to show before requiring expansion
  const MAX_MATCHES_PREVIEW = 3;

  useEffect(() => {
    if (open && sessionId && image?.uniqueId) {
      console.log('🚀 Dialog opened with image:', image);
      console.log('🚀 Dialog image uniqueId:', image?.uniqueId);
      console.log('🚀 Dialog image faceRecognition field:', image?.faceRecognition);
      console.log('🚀 Dialog image all keys:', Object.keys(image || {}));
      checkExistingMatches();
    }
  }, [open, sessionId, image?.uniqueId]);

  // Clear state when dialog closes
  useEffect(() => {
    if (!open) {
      setResults(null);
      setError(null);
      setLoading(false);
      setExpandedPersons({});
      setHasExistingMatches(false);
      setShowExistingResults(true);
      setShowSearchOptions(false);
      setPerformedFreshRecognition(false); // Reset flag when dialog closes
      // Keep search preferences for user convenience
    }
  }, [open]);

  const checkExistingMatches = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    
    try {
      console.log('🔍 Checking existing matches for image:', image);
      console.log('🔍 Image faceRecognition:', image?.faceRecognition);
      console.log('🔍 Image faceRecognition matches:', image?.faceRecognition?.matches);
      
      // Check multiple possible locations for face recognition matches
      let existingMatches = null;
      
      // Check primary location: image.faceRecognition.matches
      if (image?.faceRecognition?.matches && Array.isArray(image.faceRecognition.matches) && image.faceRecognition.matches.length > 0) {
        existingMatches = image.faceRecognition.matches;
        console.log('✅ Found existing matches in faceRecognition.matches:', existingMatches.length);
      }
      // Check alternative location: image.matches (sometimes the structure might be flattened)
      else if (image?.matches && Array.isArray(image.matches) && image.matches.length > 0) {
        existingMatches = image.matches;
        console.log('✅ Found existing matches in matches:', existingMatches.length);
      }
      // Check if face recognition was run but returned no matches
      else if (image?.faceRecognition && (image.faceRecognition.personFound === false || image.faceRecognition.matches === null)) {
        console.log('🔍 Face recognition was run but no matches found');
        // Create an empty result to show "no matches" instead of running fresh recognition
        const result = {
          matches: [],
          matchesByPerson: {},
          totalMatches: 0,
          referencePersons: [] // Will be populated if needed
        };
        
        setResults(result);
        setHasExistingMatches(true);
        setShowExistingResults(true);
        setLoading(false);
        return;
      }
      
      if (existingMatches && existingMatches.length > 0) {
        console.log('🔍 Processing existing matches:', existingMatches);
        
        // Group matches by person name (similar to backend grouping)
        const matchesByPerson = {};
        existingMatches.forEach((match, index) => {
          console.log(`🔍 Processing match ${index}:`, match);
          console.log(`🔍 Match personName:`, match.personName);
          
          const personName = match.personName;
          if (!personName) {
            console.warn('⚠️ Match missing personName:', match);
            return; // Skip matches without personName
          }
          
          if (!matchesByPerson[personName]) {
            matchesByPerson[personName] = [];
          }
          matchesByPerson[personName].push(match);
        });

        const result = {
          matches: existingMatches,
          matchesByPerson: matchesByPerson,
          totalMatches: existingMatches.length,
          referencePersons: [], // Will be populated if needed
          searchMode: image?.faceRecognition?.searchMode || { // Include search mode from database
            mode: 'previous',
            searchMainFace: true,
            searchAllFaces: true
          }
        };

        console.log('✅ Formatted existing matches result:', result);
        console.log('✅ existingMatches:', existingMatches);
        console.log('✅ matchesByPerson:', matchesByPerson);
        console.log('✅ matchesByPerson keys:', Object.keys(matchesByPerson));
        console.log('✅ totalMatches:', existingMatches.length);
        setResults(result);
        setHasExistingMatches(true);
        setShowExistingResults(true);
      } else {
        console.log('❌ No existing matches found, running fresh recognition');
        // No existing matches, run fresh recognition
        await performFreshRecognition();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to check existing matches');
    } finally {
      setLoading(false);
    }
  };

  const performFreshRecognition = async () => {
    console.log('🔄 Running fresh face recognition...');
    console.log('🔄 Search options:', { searchMainFace, searchAllFaces });
    
    // Validate search options
    if (!searchMainFace && !searchAllFaces) {
      setError('Please select at least one search option (Main Face or All Faces)');
      return;
    }
    
    setLoading(true);
    setError(null);
    setShowExistingResults(false);
    
    try {
      // Prepare search mode parameters
      const searchMode = {
        searchMainFace,
        searchAllFaces,
        mode: searchMainFace && searchAllFaces ? 'both' : searchMainFace ? 'main' : 'all'
      };
      
      const result = await faceRecognitionService.findMatchesInImage(sessionId, image.uniqueId, {
        searchMode: searchMode.mode,
        searchMainFace,
        searchAllFaces
      });
      
      console.log('✅ Fresh recognition result:', result);
      
      // Add search mode info to result for display
      result.searchMode = searchMode;
      
      setResults(result);
      setHasExistingMatches(false);
      setPerformedFreshRecognition(true); // Mark that fresh recognition was performed
      
      // Don't call onSuccess here - let the user close the dialog manually
      // The onSuccess will be called when the dialog is closed via the Close button
    } catch (err) {
      console.error('❌ Fresh recognition error:', err);
      setError(err.response?.data?.error || 'Failed to find matches in image');
    } finally {
      setLoading(false);
    }
  };

  const handleRunNewRecognition = () => {
    // Show search options when running new recognition
    setShowSearchOptions(true);
    // Don't automatically run recognition - let user choose options first
  };

  // Handle dialog close - call onSuccess to refresh parent data only if new recognition was performed
  const handleClose = () => {
    // Only refresh parent data if we performed fresh recognition
    if (performedFreshRecognition && typeof onSuccess === 'function') {
      onSuccess();
    }
    // Close the dialog
    onClose();
  };

  // Toggle expanded state for a specific person
  const togglePersonExpanded = (personName) => {
    setExpandedPersons(prev => ({
      ...prev,
      [personName]: !prev[personName]
    }));
  };

  // Calculate match percentage for display
  const getConfidenceDisplay = (confidence, similarity) => {
    // Use similarity if available, otherwise use confidence
    const matchPercentage = Math.round(similarity || confidence || 0);
    
    // Return chip with color based on confidence
    let color = 'error';
    if (matchPercentage >= 90) color = 'success';
    else if (matchPercentage >= 70) color = 'primary';
    else if (matchPercentage >= 50) color = 'warning';
    
    return (
      <Chip 
        size="small" 
        color={color}
        label={`${matchPercentage}% match`}
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <FaceIcon sx={{ mr: 1 }} />
          Face Matches in "{image?.name || 'Image'}"
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Image Preview */}
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{ mr: 2 }}>
            <AuthenticatedImage
              imageId={image?.uniqueId}
              type="thumbnail"
              alt={image?.name || 'Selected image'}
              sx={{
                width: 80,
                height: 80,
                objectFit: 'cover',
                borderRadius: 1,
                border: '2px solid',
                borderColor: 'primary.main'
              }}
            />
          </Box>
          <Box>
            <Typography variant="h6">
              {image?.name || 'Selected Image'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Searching for faces matching your reference persons
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Search Options */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 2 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Face Recognition Options
              </Typography>
              <Button
                size="small"
                onClick={() => setShowSearchOptions(!showSearchOptions)}
                startIcon={showSearchOptions ? <VisibilityOffIcon /> : <VisibilityIcon />}
              >
                {showSearchOptions ? 'Hide' : 'Show'} Options
              </Button>
            </Box>
            
            {showSearchOptions && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose which faces to search for in this image:
                </Typography>
                
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={searchMainFace}
                        onChange={(e) => setSearchMainFace(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Search Main Face</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Find matches for the largest/most prominent face in the image
                        </Typography>
                      </Box>
                    }
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={searchAllFaces}
                        onChange={(e) => setSearchAllFaces(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Search All Faces</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Find matches for all detected faces in the image
                        </Typography>
                      </Box>
                    }
                  />
                </FormGroup>
                
                {!searchMainFace && !searchAllFaces && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Please select at least one search option to proceed.
                    </Typography>
                  </Alert>
                )}
                
                {(searchMainFace || searchAllFaces) && (
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={performFreshRecognition}
                      disabled={loading}
                      startIcon={<SearchIcon />}
                    >
                      Start Search
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Show status and re-run option */}
        {!loading && results && hasExistingMatches && showExistingResults && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">
                Showing previously found matches for this image. 
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleRunNewRecognition}
                sx={{ ml: 2 }}
              >
                Configure New Search
              </Button>
            </Box>
          </Alert>
        )}

        {!loading && results && !hasExistingMatches && !showExistingResults && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Fresh face recognition completed for this image.
            </Typography>
          </Alert>
        )}

        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {showExistingResults ? 'Checking for existing matches...' : 'Running face recognition...'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {showExistingResults 
                ? 'Looking for previously found matches in this image'
                : 'Searching for faces matching your reference persons'
              }
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {results && !loading && (
          <Box>
            {results.totalMatches === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  No faces matching your reference persons were found in this image.
                </Typography>
              </Alert>
            ) : (
              <>
                {(() => {
                  // Calculate unique persons count
                  const uniquePersonsCount = results.matchesByPerson ? Object.keys(results.matchesByPerson).length : 0;
                  const uniquePersonNames = results.matchesByPerson ? Object.keys(results.matchesByPerson) : [];
                  
                  return (
                    <>
                      <Typography variant="h6" gutterBottom>
                        Found {uniquePersonsCount} unique match{uniquePersonsCount !== 1 ? 'es' : ''} 
                        {uniquePersonNames.length > 0 && ` - ${uniquePersonNames.join(', ')}`}
                        {results.referencePersons?.length > 0 && ` (from ${results.referencePersons.length} reference person${results.referencePersons.length !== 1 ? 's' : ''})`}
                      </Typography>
                      
                      {/* Display search mode used */}
                      {(results.searchMode || hasExistingMatches) && (
                        <Box display="flex" alignItems="center" mb={2}>
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Search mode:
                          </Typography>
                          {hasExistingMatches ? (
                            <Chip 
                              size="small" 
                              variant="outlined" 
                              color="info"
                              label="Previous Results"
                              icon={<PhotoLibraryIcon />}
                            />
                          ) : results.searchMode ? (
                            <>
                              {results.searchMode.searchMainFace && (
                                <Chip 
                                  size="small" 
                                  variant="outlined" 
                                  color="primary"
                                  label="Main Face"
                                  sx={{ mr: 1 }}
                                />
                              )}
                              {results.searchMode.searchAllFaces && (
                                <Chip 
                                  size="small" 
                                  variant="outlined" 
                                  color="secondary"
                                  label="All Faces"
                                />
                              )}
                              {results.searchMode.mode && (
                                <Chip 
                                  size="small" 
                                  variant="filled" 
                                  color="default"
                                  label={`Mode: ${results.searchMode.mode}`}
                                  sx={{ ml: 1, fontSize: '0.65rem' }}
                                />
                              )}
                            </>
                          ) : null}
                        </Box>
                      )}
                    </>
                  );
                })()}

                {/* Display matches grouped by person */}
                {results.matchesByPerson && Object.keys(results.matchesByPerson).length > 0 ? (
                  <Box>
                    {/* Find the main person (highest similarity) */}
                    {(() => {
                      if (!results.matchesByPerson || typeof results.matchesByPerson !== 'object') {
                        return null;
                      }
                      
                      // Calculate how many unique persons we have
                      const uniquePersonsCount = Object.keys(results.matchesByPerson).length;
                      
                      // Find the main person (highest similarity) - only if there are multiple persons
                      let mainPerson = null;
                      let highestSimilarity = 0;
                      
                      if (uniquePersonsCount > 1) {
                        Object.entries(results.matchesByPerson).forEach(([personName, personMatches]) => {
                          if (Array.isArray(personMatches) && personMatches.length > 0) {
                            const bestMatch = personMatches.reduce((best, current) => 
                              (current.similarity || 0) > (best.similarity || 0) ? current : best
                            );
                            if ((bestMatch.similarity || 0) > highestSimilarity) {
                              highestSimilarity = bestMatch.similarity || 0;
                              mainPerson = personName;
                            }
                          }
                        });
                      }
                      
                      return Object.keys(results.matchesByPerson).map((personName) => {
                        const personMatches = results.matchesByPerson[personName];
                        if (!Array.isArray(personMatches)) {
                          return null;
                        }
                        
                        const isExpanded = expandedPersons[personName] || false;
                        const hasMoreMatches = personMatches.length > MAX_MATCHES_PREVIEW;
                        const displayMatches = isExpanded ? personMatches : personMatches.slice(0, MAX_MATCHES_PREVIEW);
                        const isMainPerson = (uniquePersonsCount > 1) && (personName === mainPerson);
                        
                        return (
                          <Paper key={personName} elevation={2} sx={{ 
                            p: 2, 
                            mb: 2,
                            border: isMainPerson ? '2px solid' : 'none',
                            borderColor: isMainPerson ? 'primary.main' : 'transparent'
                          }}>
                            <Box display="flex" alignItems="center" mb={2}>
                              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="h6">
                                {personName}
                              </Typography>
                              {isMainPerson && (
                                <Chip 
                                  label="Main Person"
                                  color="primary"
                                  size="small"
                                  sx={{ ml: 1, fontWeight: 'bold' }}
                                />
                              )}
                              <Chip 
                                label={`${personMatches.length} match${personMatches.length !== 1 ? 'es' : ''}`}
                                color="primary"
                                size="small"
                                variant={isMainPerson ? "filled" : "outlined"}
                                sx={{ 
                                  ml: 2,
                                  cursor: hasMoreMatches ? 'pointer' : 'default',
                                  '&:hover': hasMoreMatches ? { backgroundColor: 'primary.dark' } : {}
                                }}
                                onClick={hasMoreMatches ? () => togglePersonExpanded(personName) : undefined}
                                onDelete={hasMoreMatches ? () => togglePersonExpanded(personName) : undefined}
                                deleteIcon={hasMoreMatches ? (isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />) : undefined}
                              />
                            </Box>
                            
                            <Grid container spacing={2}>
                            {displayMatches.map((match, index) => (
                              <Grid item xs={12} key={`${personName}-match-${index}`}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" p={1} 
                                     sx={{ backgroundColor: 'grey.50', borderRadius: 1 }}>
                                  <Box>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        Match {index + 1}
                                      </Typography>
                                      {match.searchType && (
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          color={match.searchType === 'main_face' ? 'primary' : 'secondary'}
                                          label={match.searchType === 'main_face' ? 'Main Face' : 'All Faces'}
                                          sx={{ fontSize: '0.65rem', height: '18px' }}
                                        />
                                      )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Confidence: {Math.round(match.confidence || 0)}%
                                    </Typography>
                                    {match.boundingBox && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Position: {Math.round(match.boundingBox.Left * 100)}% left, {Math.round(match.boundingBox.Top * 100)}% top
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box>
                                    {getConfidenceDisplay(match.confidence, match.similarity)}
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                            
                            {/* Show expand/collapse indicator if there are more matches */}
                            {hasMoreMatches && (
                              <Grid item xs={12}>
                                <Box 
                                  display="flex" 
                                  justifyContent="center" 
                                  alignItems="center" 
                                  p={1}
                                  sx={{ 
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'action.hover' },
                                    borderRadius: 1
                                  }}
                                  onClick={() => togglePersonExpanded(personName)}
                                >
                                  {isExpanded ? (
                                    <>
                                      <ExpandLessIcon sx={{ mr: 1 }} />
                                      <Typography variant="body2">Show Less</Typography>
                                    </>
                                  ) : (
                                    <>
                                      <ExpandMoreIcon sx={{ mr: 1 }} />
                                      <Typography variant="body2">
                                        Show {personMatches.length - MAX_MATCHES_PREVIEW} More Match{personMatches.length - MAX_MATCHES_PREVIEW !== 1 ? 'es' : ''}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      );
                    }).filter(Boolean); // Filter out null values
                  })()}
                  </Box>
                ) : (
                  /* Fallback display for matches not grouped by person */
                  <Box>
                    <Grid container spacing={2}>
                      {results.matches?.map((match, index) => (
                        <Grid item xs={12} sm={6} key={`match-${index}`}>
                          <Paper elevation={1} sx={{ p: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">
                                  {match.personName || `Match #${index + 1}`}
                                </Typography>
                                {match.searchType && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color={match.searchType === 'main_face' ? 'primary' : 'secondary'}
                                    label={match.searchType === 'main_face' ? 'Main Face' : 'All Faces'}
                                    sx={{ fontSize: '0.65rem', height: '20px' }}
                                  />
                                )}
                              </Box>
                              {getConfidenceDisplay(match.confidence, match.similarity)}
                            </Box>
                            
                            <Typography variant="caption" color="text.secondary" display="block">
                              Confidence: {Math.round(match.confidence || 0)}%
                            </Typography>
                            {match.boundingBox && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Position: {Math.round(match.boundingBox.Left * 100)}% left, {Math.round(match.boundingBox.Top * 100)}% top
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Reference persons summary */}
                {results.referencePersons?.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="body2" color="text.secondary">
                      Searched for {results.referencePersons.length} reference person{results.referencePersons.length !== 1 ? 's' : ''}: {' '}
                      {results.referencePersons.map(p => p.name).join(', ')}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
        {results && hasExistingMatches && showExistingResults && (
          <Button 
            onClick={handleRunNewRecognition}
            variant="outlined"
            disabled={loading}
          >
            Configure New Search
          </Button>
        )}
        {!results && !hasExistingMatches && (
          <Button 
            onClick={() => setShowSearchOptions(true)}
            variant="outlined"
            disabled={loading}
          >
            Show Search Options
          </Button>
        )}
        {results && results.totalMatches > 0 && (
          <Button 
            onClick={() => {
              // Optional: Add functionality to view full image with face boxes highlighted
            }}
            variant="outlined"
          >
            View Full Image
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SingleImageFaceRecognitionDialog;
